/* M1 Dashboards — chart primitives (React ports of the mockup PS.donut + the
   lifted CSS chart shapes: .vbars, .barrow, .agebar, .minibar, .pbar).
   These render the exact mockup markup + class names so the scoped CSS applies
   1:1, with a one-shot grow-in animation on mount (mirrors the mockup
   requestAnimationFrame height/width tweens). Dummy-data only, no network. */
import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {inr} from '../components'

/* ───────── Donut (PS.donut port) ───────── */
export function Donut({segments, unit = 'total', money = false}) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0)
  const R = 58
  const C = 2 * Math.PI * R
  const cx = 72
  const cy = 72
  let acc = 0
  const arcs = segments.map((s, i) => {
    if (!s.value) return null
    const len = (s.value / (total || 1)) * C
    const pct = total ? Math.round((s.value * 100) / total) : 0
    const off = (-acc / (total || 1)) * C
    acc += s.value
    return (
      <circle
        key={i}
        className='donut-seg'
        cx={cx}
        cy={cy}
        r={R}
        fill='none'
        stroke={s.color}
        strokeWidth='20'
        strokeDasharray={`${len.toFixed(2)} ${(C - len).toFixed(2)}`}
        strokeDashoffset={off.toFixed(2)}
        data-tip={`${s.label} — ${money ? inr(s.value) : s.value} (${pct}%)`}
      >
        <title>{`${s.label} — ${money ? inr(s.value) : s.value} (${pct}%)`}</title>
      </circle>
    )
  })
  const hasArc = arcs.some(Boolean)
  return (
    <div className='donut-wrap'>
      <div className='donut-svgwrap'>
        <svg width='144' height='144' viewBox='0 0 144 144' className='donut-svg'>
          <g transform={`rotate(-90 ${cx} ${cy})`}>
            {hasArc ? (
              arcs
            ) : (
              <circle cx={cx} cy={cy} r={R} fill='none' stroke='#EEF2F6' strokeWidth='20' />
            )}
          </g>
          <text
            x={cx}
            y={cy - 3}
            textAnchor='middle'
            dominantBaseline='central'
            className='donut-total'
          >
            {money ? inr(total) : total}
          </text>
          <text
            x={cx}
            y={cy + 15}
            textAnchor='middle'
            dominantBaseline='central'
            className='donut-unit'
          >
            {unit}
          </text>
        </svg>
      </div>
      <div className='legend'>
        {segments.map((s, i) => {
          const pct = total ? Math.round((s.value * 100) / total) : 0
          return (
            <div className='li' key={i}>
              <span className='sw' style={{background: s.color}} />
              {s.label}
              <span className='v'>{money ? inr(s.value) : s.value}</span>
              <span className='a'>{pct}%</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
Donut.propTypes = {
  segments: PropTypes.array.isRequired,
  unit: PropTypes.string,
  money: PropTypes.bool,
}

/* ───────── Vertical bar trend (.vbars) — grows in on mount ───────── */
export function VBars({columns, legend}) {
  const ref = useRef(null)
  useEffect(() => {
    const host = ref.current
    if (!host) return
    const bars = host.querySelectorAll('.b[data-h]')
    const id = requestAnimationFrame(() => {
      bars.forEach((b) => {
        b.style.height = b.dataset.h + '%'
      })
    })
    return () => cancelAnimationFrame(id)
  }, [columns])
  const max = Math.max(1, ...columns.map((c) => Math.max(...c.bars.map((b) => b.value))))
  return (
    <>
      <div className='vbars' ref={ref}>
        {columns.map((c, i) => (
          <div className='vbcol' key={i}>
            <div style={{fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap'}}>
              {c.bars.map((b, j) => (
                <React.Fragment key={j}>
                  {j > 0 ? <span className='muted'> / </span> : null}
                  <span style={{color: b.color}}>{b.value}</span>
                </React.Fragment>
              ))}
            </div>
            <div className='stack'>
              {c.bars.map((b, j) => (
                <div
                  key={j}
                  className='b'
                  data-h={((b.value / max) * 100).toFixed(1)}
                  style={{height: 0, background: b.color}}
                  title={`${b.label}: ${b.value}`}
                />
              ))}
            </div>
            <div className='mlbl'>{c.label}</div>
          </div>
        ))}
      </div>
      {legend ? (
        <div className='age-legend' style={{justifyContent: 'center'}}>
          {legend.map((l, i) => (
            <div className='li' key={i}>
              <span className='sw' style={{background: l.color}} />
              {l.label}
            </div>
          ))}
        </div>
      ) : null}
    </>
  )
}
VBars.propTypes = {columns: PropTypes.array.isRequired, legend: PropTypes.array}

/* ───────── horizontal split mini-bar (.minibar) ───────── */
export function MiniBar({segments, height = 9}) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0) || 1
  return (
    <div className='minibar' style={{height}}>
      {segments.map((s, i) =>
        s.value ? (
          <span
            key={i}
            style={{width: `${((s.value / total) * 100).toFixed(1)}%`, background: s.color}}
            title={s.label || undefined}
          />
        ) : null
      )}
    </div>
  )
}
MiniBar.propTypes = {segments: PropTypes.array.isRequired, height: PropTypes.number}

/* ───────── stacked ageing bar (.agebar) ───────── */
export function AgeBar({segments, width = 100}) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0) || 1
  return (
    <div className='agebar' style={{width: `${width.toFixed(1)}%`}}>
      {segments.map((s, i) =>
        s.value ? (
          <span
            key={i}
            style={{width: `${((s.value / total) * 100).toFixed(1)}%`, background: s.color}}
            title={`${s.label}: ${s.value}`}
          />
        ) : null
      )}
    </div>
  )
}
AgeBar.propTypes = {segments: PropTypes.array.isRequired, width: PropTypes.number}

/* ───────── progress bar (.pbar) — grows in on mount ───────── */
export function PBar({pct, color, large = true, tip}) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const id = requestAnimationFrame(() => {
      el.style.width = pct + '%'
    })
    return () => cancelAnimationFrame(id)
  }, [pct])
  return (
    <div className={large ? 'pbar pbar-lg' : 'pbar'} title={tip}>
      <span ref={ref} style={{width: 0, background: color}} />
    </div>
  )
}
PBar.propTypes = {
  pct: PropTypes.number.isRequired,
  color: PropTypes.string,
  large: PropTypes.bool,
  tip: PropTypes.string,
}
